import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

const ThreadsContext = createContext();

export const useThreads = () => useContext(ThreadsContext);

const THREADS_APP_ID = import.meta.env.VITE_THREADS_APP_ID || import.meta.env.VITE_FACEBOOK_APP_ID;
const THREADS_REDIRECT_URI = import.meta.env.VITE_THREADS_REDIRECT_URI;
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

// Threads-specific scopes
const SCOPES = [
  'threads_basic',
  'threads_content_publish',
  'threads_manage_insights',
  'threads_manage_replies',
  'threads_read_replies',
  'threads_manage_mentions',
  'threads_keyword_search',
  'threads_delete',
  'threads_profile_discovery',
  'threads_location_tagging',
].join(',');

const generateRandomString = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
    .substring(0, length);
};

export const ThreadsProvider = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = useSelector((state) => state.auth?.user);

  useEffect(() => {
    if (user) {
      loadConnectedAccounts();
    } else {
      setAccounts([]);
      setLoading(false);
    }
  }, [user]);

  const loadConnectedAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/threads/accounts`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to load Threads accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const connectThreads = useCallback(() => {
    try {
      const state = `th_${generateRandomString(32)}`;
      sessionStorage.setItem('threads_oauth_state', state);

      // Threads has its own OAuth provider (not facebook.com)
      const authUrl = new URL('https://threads.net/oauth/authorize');
      authUrl.searchParams.set('client_id', THREADS_APP_ID);
      authUrl.searchParams.set('redirect_uri', THREADS_REDIRECT_URI);
      authUrl.searchParams.set('scope', SCOPES);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('state', state);

      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Failed to start Threads OAuth:', err);
      setError('Failed to start Threads connection');
    }
  }, []);

  const handleCallback = useCallback(async (code, state) => {
    const savedState = sessionStorage.getItem('threads_oauth_state');
    if (!savedState || state !== savedState) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/threads/oauth/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code,
          redirect_uri: THREADS_REDIRECT_URI,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect Threads account');
      }

      const data = await response.json();

      sessionStorage.removeItem('threads_oauth_state');

      return {
        availableAccounts: data.available_accounts,
        tokenRef: data.token_ref,
      };
    } catch (err) {
      sessionStorage.removeItem('threads_oauth_state');
      throw err;
    }
  }, []);

  const selectAccount = useCallback(async (tokenRef) => {
    try {
      const response = await fetch(`${API_BASE_URL}/threads/oauth/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token_ref: tokenRef,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to select Threads account');
      }

      const data = await response.json();

      // Dedupe by id: re-connecting an account already linked to this org returns
      // the SAME account (already_connected=true), not a new one — update in place.
      setAccounts(prev => {
        const exists = prev.some(a => a.id === data.account.id);
        return exists
          ? prev.map(a => (a.id === data.account.id ? data.account : a))
          : [...prev, data.account];
      });

      return { account: data.account, alreadyConnected: !!data.already_connected };
    } catch (err) {
      console.error('Failed to select Threads account:', err);
      throw err;
    }
  }, []);

  const disconnectAccount = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setAccounts(prev => prev.filter(a => a.id !== accountId));
      } else {
        throw new Error('Failed to disconnect account');
      }
    } catch (err) {
      console.error('Failed to disconnect Threads account:', err);
      setError(err.message);
    }
  }, []);

  const refreshAccountStats = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAccounts(prev =>
          prev.map(a => (a.id === accountId ? { ...a, ...data.account } : a))
        );
        return data.account;
      } else {
        throw new Error('Failed to refresh stats');
      }
    } catch (err) {
      console.error('Failed to refresh Threads stats:', err);
      setError(err.message);
    }
  }, []);

  const fetchAccountThreads = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/threads`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch threads');
      }
    } catch (err) {
      console.error('Failed to fetch Threads:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const getAccountById = useCallback((accountId) => {
    return accounts.find(a => a.id === accountId);
  }, [accounts]);

  const publishThread = useCallback(async (accountId, text, locationId) => {
    const body = { text };
    if (locationId) body.location_id = locationId;
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to publish thread');
    }
    return response.json();
  }, []);

  const searchLocations = useCallback(async (accountId, query) => {
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/locations?q=${encodeURIComponent(query)}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to search locations');
    }
    return response.json();
  }, []);

  const getInsights = useCallback(async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/insights`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch insights');
    }
    return response.json();
  }, []);

  const getThreadInsights = useCallback(async (accountId, threadId) => {
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/threads/${threadId}/insights`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch thread insights');
    }
    return response.json();
  }, []);

  const getThreadReplies = useCallback(async (accountId, threadId) => {
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/threads/${threadId}/replies`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch replies');
    }
    return response.json();
  }, []);

  const replyToThread = useCallback(async (accountId, threadId, text) => {
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/threads/${threadId}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ text }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to reply');
    }
    return response.json();
  }, []);

  const hideReply = useCallback(async (accountId, replyId, hide = true) => {
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/replies/${replyId}/hide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ hide }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to manage reply');
    }
    return response.json();
  }, []);

  const getMentions = useCallback(async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/mentions`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch mentions');
    }
    return response.json();
  }, []);

  const searchThreads = useCallback(async (accountId, query) => {
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/search?q=${encodeURIComponent(query)}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to search');
    }
    return response.json();
  }, []);

  const deleteThread = useCallback(async (accountId, threadId) => {
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/threads/${threadId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete thread');
    }
    return response.json();
  }, []);

  const getProfileDiscovery = useCallback(async (accountId, username = '') => {
    const params = username ? `?username=${encodeURIComponent(username)}` : '';
    const response = await fetch(`${API_BASE_URL}/threads/accounts/${accountId}/profile${params}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch profile');
    }
    return response.json();
  }, []);

  return (
    <ThreadsContext.Provider
      value={{
        accounts,
        loading,
        error,
        connectThreads,
        handleCallback,
        selectAccount,
        disconnectAccount,
        refreshAccountStats,
        loadConnectedAccounts,
        fetchAccountThreads,
        getAccountById,
        publishThread,
        searchLocations,
        getInsights,
        getThreadInsights,
        getThreadReplies,
        replyToThread,
        hideReply,
        getMentions,
        searchThreads,
        deleteThread,
        getProfileDiscovery,
      }}
    >
      {children}
    </ThreadsContext.Provider>
  );
};

export default ThreadsContext;
